// The cabinet's query language: the small subset of the supabase-js builder the
// screens use, as a plain descriptor, and the one function that turns a
// descriptor into parameterised SQL. Shared by both backends so they can never
// drift apart:
//   development  devdb.js runs the SQL on PGlite in the browser
//   production   gcp.js posts the descriptor to /api/cabinet/db; cabinet-api
//                (tools/gcp/cabinet-api) validates it with this same file and
//                runs it on Cloud SQL as the signed-in person, under RLS.
// No DOM, no Node APIs: it runs in both.
//
// Supported: from(t).select(cols, {count, head}).eq/neq/in/is/not/gt/gte/lt/lte/ilike
// .order(col, {ascending}).limit(n).range(a,b).single()/.maybeSingle();
// insert(rows).select(); update(v).eq(...); upsert(rows, {onConflict}); delete().eq(...).
// Not supported (by design — keep queries simple): embedded joins in select().

const IDENT = /^[a-z_][a-z0-9_]*$/i;
const OPS = new Set(['select', 'insert', 'upsert', 'update', 'delete']);
const CMP = { eq: '=', gt: '>', gte: '>=', lt: '<', lte: '<=' };
export const MAX_ROWS = 10000;
const MAX_WRITE_ROWS = 2000;

function fail(msg) { throw Object.assign(new Error(msg), { code: 'PGRST100' }); }
export function ident(s) {
    if (typeof s !== 'string' || !IDENT.test(s)) fail('bad identifier ' + String(s).slice(0, 60));
    return '"' + s + '"';
}
const isPlain = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const natural = (n, name) => {
    if (n == null) return null;
    if (!Number.isInteger(n) || n < 0) fail('bad ' + name);
    return n;
};

export class Query {
    // exec(desc) → Promise<{data, error, count}>, supplied by the backend.
    constructor(table, exec) {
        this.exec_ = exec;
        this.d = { t: table, op: 'select', cols: '*', count: null, head: false, where: [], order: [], limit: null, offset: null, one: null, ret: null, values: null, conflict: null };
    }
    select(cols = '*', opts = {}) {
        if (this.d.op === 'select') { this.d.cols = cols; this.d.count = opts.count || null; this.d.head = !!opts.head; }
        else this.d.ret = cols;
        return this;
    }
    insert(rows) { this.d.op = 'insert'; this.d.values = Array.isArray(rows) ? rows : [rows]; return this; }
    upsert(rows, opts = {}) { this.d.op = 'upsert'; this.d.values = Array.isArray(rows) ? rows : [rows]; this.d.conflict = opts.onConflict || null; return this; }
    update(v) { this.d.op = 'update'; this.d.values = v; return this; }
    delete() { this.d.op = 'delete'; return this; }
    f(kind, c, v) { this.d.where.push([kind, c, v === undefined ? null : v]); return this; }
    eq(c, v) { return this.f('eq', c, v); }
    neq(c, v) { return this.f('neq', c, v); }
    gt(c, v) { return this.f('gt', c, v); }
    gte(c, v) { return this.f('gte', c, v); }
    lt(c, v) { return this.f('lt', c, v); }
    lte(c, v) { return this.f('lte', c, v); }
    ilike(c, v) { return this.f('ilike', c, v); }
    in(c, vs) { return this.f('in', c, vs); }
    is(c, v) { return this.f('is', c, v); }
    not(c, op, v) { return op === 'is' && v === null ? this.f('notnull', c, null) : this.f('not', c, v); }
    order(c, o = {}) { this.d.order.push([c, o.ascending !== false]); return this; }
    limit(n) { this.d.limit = n; return this; }
    range(a, b) { this.d.offset = a; this.d.limit = b - a + 1; return this; }
    single() { this.d.one = 'single'; return this; }
    maybeSingle() { this.d.one = 'maybe'; return this; }
    then(res, rej) { return this.exec_(this.d).then(res, rej); }
}

// Descriptor → { text, params }. Everything a client sends is checked here:
// identifiers against a strict pattern (no schema-qualified names, no quoting
// tricks), operators against a closed list, values only ever as parameters.
// `tables`, when given, is the set of relations the caller may name.
export function buildSql(d, { tables = null } = {}) {
    if (!isPlain(d)) fail('bad query');
    if (!OPS.has(d.op)) fail('bad operation');
    const T = ident(d.t);
    if (tables && !tables.has(d.t)) fail(`relation "${d.t}" is not available`);
    const params = [];
    const p = (v) => { params.push(v); return '$' + params.length; };
    const pv = (v) => p(isPlain(v) ? JSON.stringify(v) : v);
    const cols = (c) => {
        if (c === '*' || c == null) return '*';
        if (typeof c !== 'string') fail('bad columns');
        const list = c.split(',').map(s => s.trim()).filter(Boolean);
        if (!list.length) fail('bad columns');
        return list.map(ident).join(', ');
    };
    const where = (Array.isArray(d.where) ? d.where : fail('bad filter')).map((w) => {
        if (!Array.isArray(w) || w.length !== 3) fail('bad filter');
        const [kind, c, v] = w;
        const C = ident(c);
        if (CMP[kind]) return `${C} ${CMP[kind]} ${p(v)}`;
        switch (kind) {
            case 'neq': return `${C} is distinct from ${p(v)}`;
            case 'ilike': return `${C}::text ilike ${p(v)}`;
            case 'in': if (!Array.isArray(v)) fail('in() needs a list'); return `${C} = any(${p(v)})`;
            case 'is': if (v !== null && v !== true && v !== false) fail('is() takes null, true or false'); return `${C} is ${v === null ? 'null' : v ? 'true' : 'false'}`;
            case 'notnull': return `${C} is not null`;
            case 'not': return `not (${C} = ${p(v)})`;
            default: fail('bad filter ' + String(kind).slice(0, 20));
        }
    });
    const W = where.length ? ' where ' + where.join(' and ') : '';
    const ret = d.ret ? ' returning ' + cols(d.ret) : '';

    if (d.op === 'select') {
        if (d.head) return { text: `select count(*)::int as n from ${T}${W}`, params };
        let s = `select ${cols(d.cols)}${d.count ? ', count(*) over ()::int as __count' : ''} from ${T}${W}`;
        const order = Array.isArray(d.order) ? d.order : fail('bad order');
        if (order.length) s += ' order by ' + order.map(([c, asc]) => `${ident(c)} ${asc === false ? 'desc' : 'asc'} nulls last`).join(', ');
        const lim = natural(d.limit, 'limit');
        const off = natural(d.offset, 'offset');
        s += ' limit ' + Math.min(lim ?? MAX_ROWS, MAX_ROWS);
        if (off != null) s += ' offset ' + off;
        return { text: s, params };
    }
    if (d.op === 'insert' || d.op === 'upsert') {
        if (!Array.isArray(d.values) || !d.values.length || d.values.length > MAX_WRITE_ROWS || !d.values.every(isPlain)) fail('bad rows');
        const keys = [...new Set(d.values.flatMap(Object.keys))];
        if (!keys.length) fail('bad rows');
        const rows = d.values.map(r => '(' + keys.map(k => r[k] === undefined ? 'default' : pv(r[k])).join(', ') + ')');
        let s = `insert into ${T} (${keys.map(ident).join(', ')}) values ${rows.join(', ')}`;
        if (d.op === 'upsert') {
            const on = String(d.conflict || 'id').split(',').map(x => x.trim());
            const upd = keys.filter(k => !on.includes(k));
            s += ` on conflict (${on.map(ident).join(', ')}) ` + (upd.length ? 'do update set ' + upd.map(k => `${ident(k)} = excluded.${ident(k)}`).join(', ') : 'do nothing');
        }
        return { text: s + ret, params };
    }
    if (d.op === 'update') {
        if (!isPlain(d.values) || !Object.keys(d.values).length) fail('bad update');
        const set = Object.entries(d.values).map(([k, v]) => `${ident(k)} = ${pv(v)}`).join(', ');
        return { text: `update ${T} set ${set}${W}${ret}`, params };
    }
    return { text: `delete from ${T}${W}${ret}`, params };
}

// Like PostgREST: writing zero rows is a no-op, not an error.
export const isNoop = (d) => (d.op === 'insert' || d.op === 'upsert') && Array.isArray(d.values) && !d.values.length;

// Rows (already JSON-friendly) → what supabase-js would return.
export function shape(d, rows, affected) {
    if (d.head) return { data: null, count: rows[0]?.n ?? 0, error: null };
    let count = null;
    if (d.count) { count = rows[0]?.__count ?? 0; rows = rows.map(({ __count, ...x }) => x); }
    if (d.op !== 'select' && !d.ret) return { data: null, error: null, count: affected ?? null };
    if (d.one) {
        if (rows.length > 1 || (d.one === 'single' && rows.length !== 1))
            return { data: null, error: { message: `expected one row, got ${rows.length}`, code: 'PGRST116' } };
        return { data: rows[0] || null, error: null };
    }
    return { data: rows, error: null, count };
}

// rpc(fn, args) → SQL. `fns`, when given, maps callable names to {setof}.
export function buildRpc(fn, args = {}, { fns = null } = {}) {
    const F = ident(fn);
    if (fns && !fns.has(fn)) fail(`function "${fn}" is not available`);
    if (!isPlain(args) && args != null) fail('bad arguments');
    const names = Object.keys(args || {});
    const text = `select * from ${F}(${names.map((k, i) => `${ident(k)} => $${i + 1}`).join(', ')})`;
    return { text, params: names.map(k => isPlain(args[k]) ? JSON.stringify(args[k]) : args[k]) };
}
export function shapeRpc(fn, rows, setof) {
    const scalar = rows.length && Object.keys(rows[0]).length === 1 && Object.keys(rows[0])[0] === fn;
    return setof ? (scalar ? rows.map(x => x[fn]) : rows) : (scalar ? rows[0]?.[fn] ?? null : rows[0] ?? null);
}

// Values as JSON would carry them (dates as ISO strings, int8 as numbers).
function norm(v) {
    if (v instanceof Date) return v.toISOString();
    if (typeof v === 'bigint') return Number(v);
    if (Array.isArray(v)) return v.map(norm);
    return v;
}
export const normRow = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, norm(v)]));
